import { useCallback, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  ValueFormatterParams,
  ICellRendererParams,
  IDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './HospitalManagement.module.css';
import { localeText } from '../../../constants/AgGridLocale';
import { Pencil, Trash2, Plus, ArrowRight, RotateCw } from 'lucide-react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IHospitalResult, ITag } from '../../../models/hospital/HospitalModel';
import NewHospital from '../hospital-files/parts/new-data/new-hospital/NewHospital';
import DeleteConfirmationDialog from '../hospital-files/parts/delete/DeleteConfirmationDialog';
import NewTagNumber from '../hospital-files/parts/new-data/new-tag/NewTagNumber';
import EditHospital from '../hospital-files/parts/edit-data/EditHospital';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Popup from '../../../components/common-components/pop-up/Popup';
import CommonDropdown from '../../../components/common-components/dropdown/CommonDropdown';
import { hospitalService } from '../../../services/modules/hospital/hospital.service';

const ActionCellRenderer = (props: ICellRendererParams<IHospitalResult>) => {
  const { popupStore, hospitalStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalStore.setSelectedHospitalToEdit(props.data);
      popupStore.setIsEditHospitalOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) {
      toast.error('Bu işlem için admin yetkisi gereklidir.');
      return;
    }
    if (props.data) {
      hospitalStore.setSelectedHospitalToDelete(props.data);
      popupStore.setIsDeleteConfirmationOpen(true);
    }
  };

  const handleSelectTags = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalStore.setSelectedHospitalForTags(props.data);
      popupStore.setIsSelectTagsOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>

      <button
        onClick={handleDelete}
        className={`${styles.deleteButton} ${!isAdmin ? styles.disabledButton : ''}`}
        title={isAdmin ? 'Sil' : 'Bu işlem için admin yetkisi gereklidir'}
        disabled={!isAdmin}
      >
        <Trash2 size={20} />
      </button>
      <button onClick={handleSelectTags} className={styles.selectButton} title="Etiket Seç">
        <ArrowRight size={20} />
      </button>
    </div>
  );
};

const SelectTagsPopup = () => {
  const { popupStore, hospitalStore, hospitalFilesStore } = useStore();
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [tags, setTags] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (popupStore.isSelectTagsOpen && hospitalStore.selectedHospitalForTags) {
      loadHospitalTags();
    }
  }, [popupStore.isSelectTagsOpen, hospitalStore.selectedHospitalForTags]);

  const loadHospitalTags = async () => {
    if (!hospitalStore.selectedHospitalForTags) return;

    setLoading(true);
    try {
      const response = await hospitalService.getTagsByHospitalId(
        hospitalStore.selectedHospitalForTags.id
      );

      if (response && Array.isArray(response)) {
        const tagOptions = response.map((tag: ITag) => ({
          value: tag.id.toString(),
          label: `${tag.prefix}`,
        }));
        setTags(tagOptions);
      } else {
        setTags([]);
        toast.warning('Bu hastane için etiket bulunamadı');
      }
    } catch (error) {
      console.error('Etiketler yüklenirken hata:', error);
      toast.error('Etiketler yüklenirken bir hata oluştu');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    if (!selectedTag) {
      toast.error('Lütfen bir etiket seçin');
      return;
    }

    if (!hospitalStore.selectedHospitalForTags) {
      toast.error('Hastane bilgisi bulunamadı');
      return;
    }

    // Hospital Files store'unda hastane ve etiket seç
    hospitalFilesStore.setSelectedHospital(hospitalStore.selectedHospitalForTags);

    // Seçilen etiketi bul ve set et
    const selectedTagObj = tags.find(tag => tag.value === selectedTag);
    if (selectedTagObj) {
      // API'den gelen tag listesinden seçilen tag'ı bul
      const apiResponse = hospitalService.getTagsByHospitalId(
        hospitalStore.selectedHospitalForTags.id
      );
      apiResponse.then(response => {
        if (response && Array.isArray(response)) {
          const foundTag = response.find(tag => tag.id.toString() === selectedTag);
          if (foundTag) {
            hospitalFilesStore.setSelectedTag(foundTag);
          }
        }
      });
    }

    // Hospital Files sayfasına yönlendir
    navigate('/hospital-files');
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
  };

  const handleClose = () => {
    popupStore.setIsSelectTagsOpen(false);
    setSelectedTag('');
    hospitalStore.setSelectedHospitalForTags(null);
  };

  return (
    <Popup isOpen={popupStore.isSelectTagsOpen} onClose={handleClose} title="Etiket Seç">
      <div className={styles.selectTagsContainer}>
        <div className={styles.hospitalInfo}>
          <h4>Hastane: {hospitalStore.selectedHospitalForTags?.hospitalName}</h4>
        </div>

        <div className={styles.dropdownContainer}>
          <label className={styles.dropdownLabel}>Etiket Seçin:</label>
          <CommonDropdown
            options={tags}
            value={selectedTag}
            onChange={value => setSelectedTag(value || '')}
            placeholder={loading ? 'Yükleniyor...' : 'Etiket seçin...'}
            disabled={loading}
          />
        </div>

        <div className={styles.buttonContainer}>
          <CommonButton
            type="primary"
            onClick={handleNavigate}
            disabled={!selectedTag || loading}
            className={styles.navigateButton}
          >
            Git
          </CommonButton>
        </div>
      </div>
    </Popup>
  );
};

const HospitalManagement = () => {
  const [gridApi, setGridApi] = useState<GridApi<IHospitalResult> | null>(null);
  const { popupStore, uiStore, hospitalStore } = useStore();

  useEffect(() => {
    uiStore.setLoading(true);
    hospitalStore.listAllHospitals();
    uiStore.setLoading(false);
  }, [hospitalStore]);

  const dataSource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          uiStore.setLoading(true);
          const result = await hospitalStore.listAllHospitals({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        } finally {
          uiStore.setLoading(false);
        }
      },
    };
  }, [hospitalStore, uiStore]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<IHospitalResult>) => {
      setGridApi(params.api);
      hospitalStore.setGridApi(params.api);
    },
    [hospitalStore]
  );

  const columnDefs = useMemo<ColDef<IHospitalResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 160,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'hospitalName',
        headerName: 'Hastane Adı',
        minWidth: 125,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'createdDate',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 125,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR');
        },
      },
      {
        field: 'hospitalCloseDate',
        headerName: 'Kapanış Tarihi',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '-';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
      {
        field: 'isHospitalClosed',
        headerName: 'Hastane Kapalı',
        width: 120,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '-';
          return params.value ? 'Evet' : 'Hayır';
        },
        cellStyle: params => {
          if (params.value === true) {
            return { color: '#dc2626', fontWeight: 'bold' };
          }
          return { color: '#059669', fontWeight: 'bold' };
        },
      },
      {
        field: 'recordCount',
        headerName: 'Kayıt Sayısı',
        width: 120,
        flex: 1,
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'greaterThan', 'lessThan'],
          defaultOption: 'equals',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) return '0';
          return params.value.toString();
        },
        cellStyle: () => {
          return {
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#1f2937',
          };
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );

  const handleNewHospital = () => {
    popupStore.setIsNewHospitalOpen(true);
  };

  const handleNewTagNumber = () => {
    popupStore.setIsNewTagOpen(true);
  };

  const refreshGrid = () => {
    if (hospitalStore.gridApi) {
      hospitalStore.gridApi.refreshServerSide();
      hospitalStore.gridApi.refreshInfiniteCache();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Hastane Yönetimi</h2>
          <div className={styles.buttonContainer}>
            <CommonButton type="primary" onClick={handleNewHospital}>
              <Plus size={20} />
              Yeni Hastane Oluştur
            </CommonButton>
            <CommonButton type="primary" onClick={handleNewTagNumber}>
              Hastane Etiket No Ekle
            </CommonButton>
            <CommonButton type="secondary" onClick={refreshGrid} leftIcon={<RotateCw size={18} />}>
              Yenile
            </CommonButton>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              rowModelType={'infinite'}
              cacheBlockSize={20}
              maxBlocksInCache={1}
              datasource={dataSource}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={20}
              rowSelection="multiple"
              rowHeight={48}
              headerHeight={48}
              animateRows={true}
              suppressColumnVirtualisation={true}
              suppressRowVirtualisation={true}
              localeText={localeText}
              getRowStyle={params => {
                if (params.data?.isHospitalClosed === true) {
                  return { backgroundColor: '#fef2f2' }; // Açık kırmızı arka plan
                }
                return undefined;
              }}
            />
          </div>
        </div>
      </div>
      {popupStore.isNewHospitalOpen && <NewHospital />}
      {popupStore.isNewTagOpen && <NewTagNumber />}
      {popupStore.isEditHospitalOpen && <EditHospital />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteConfirmationOpen}
        onClose={() => popupStore.setIsDeleteConfirmationOpen(false)}
        onConfirm={async () => {
          if (hospitalStore.selectedHospitalToDelete?.id) {
            await hospitalStore.deleteHospital(hospitalStore.selectedHospitalToDelete.id);
            hospitalStore.refreshGrid();
          }
          popupStore.setIsDeleteConfirmationOpen(false);
        }}
        title={`Hastane Silme Onayı`}
      />
      {popupStore.isSelectTagsOpen && <SelectTagsPopup />}
    </>
  );
};

export default observer(HospitalManagement);
